/**
 * Provisional World Cup knockout bracket — shown during the group stage, before the
 * official knockout fixtures are drawn and synced. Every slot is a placeholder labelled
 * by its qualification path (e.g. "1st · Group A", "3rd place", "Winner of R32 M3").
 *
 * IMPORTANT: this is NOT the official draw. The 12 group winners / runners-up and the
 * eight best third-placed teams are paired in a fixed, representative layout so users can
 * preview the bracket shape — the real matchups only lock once the group stage ends, which
 * the UI states via a prominent notice. The moment real knockout fixtures land in the feed,
 * the data-driven `buildWorldCupBracket` takes over and this is never shown.
 *
 * Distribution per the expanded 48-team format (12 groups → 32 teams in the Round of 32):
 *   12 winners + 12 runners-up + 8 best third-placed = 32 → 16 R32 ties:
 *     · 8 ties: group winner vs a best third-placed team
 *     · 4 ties: group winner vs a runner-up
 *     · 4 ties: runner-up vs runner-up
 */

import type {
  BracketMatch,
  BracketRound,
  BracketTeamSlot,
  KnockoutBracketModel,
} from '../../knockout/types/knockoutBracket.types'
import { WORLD_CUP_GROUP_STAGE_LETTERS } from '../constants/worldCupGroupStageLetters.constants'

interface ProvisionalRoundDef {
  key: string
  label: string
  /** Number of ties in this round. */
  count: number
}

/** R32 → Final, by tie count. Third place is rendered separately under the Final. */
const PROVISIONAL_ROUNDS: ProvisionalRoundDef[] = [
  { key: 'round_of_32', label: 'Round of 32', count: 16 },
  { key: 'round_of_16', label: 'Round of 16', count: 8 },
  { key: 'quarter_finals', label: 'Quarter-finals', count: 4 },
  { key: 'semi_finals', label: 'Semi-finals', count: 2 },
  { key: 'final', label: 'Final', count: 1 },
]

function placeholderSlot(label: string): BracketTeamSlot {
  return { name: null, placeholderLabel: label }
}

function placeholderMatch(
  id: string,
  home: string,
  away: string,
  isFinal: boolean,
): BracketMatch {
  return {
    id,
    home: placeholderSlot(home),
    away: placeholderSlot(away),
    status: 'upcoming',
    isFinal,
    href: null,
  }
}

/**
 * The 32 Round-of-32 entrant labels, in tie order (home, away, home, away, …).
 * Group winners and runners-up are labelled by letter; third-placed slots stay generic
 * because which group each comes from is only decided after the group stage.
 */
function roundOf32Labels(letters: readonly string[]): string[] {
  const winners = letters.map((l) => `1st · Group ${l}`)
  const runnersUp = letters.map((l) => `2nd · Group ${l}`)
  const thirds = Array.from({ length: 8 }, () => '3rd place')

  const homes: string[] = []
  const aways: string[] = []

  // 8 ties: group winner vs a third-placed team.
  for (let i = 0; i < 8; i += 1) {
    homes.push(winners[i])
    aways.push(thirds[i])
  }
  // 4 ties: group winner vs a runner-up.
  for (let i = 8; i < 12; i += 1) {
    homes.push(winners[i])
    aways.push(runnersUp[i - 8])
  }
  // 4 ties: runner-up vs runner-up (the remaining eight runners-up).
  for (let i = 0; i < 4; i += 1) {
    homes.push(runnersUp[4 + i])
    aways.push(runnersUp[8 + i])
  }

  // Flatten to home, away, home, away, … so callers can pair them positionally.
  const flat: string[] = []
  for (let i = 0; i < homes.length; i += 1) {
    flat.push(homes[i], aways[i])
  }
  return flat
}

/**
 * Build the full provisional bracket. `letters` defaults to the official 12 group letters
 * but accepts the groups actually present (derived from live data) so the preview reflects
 * the real tournament when fewer/renamed groups exist.
 */
export function buildProvisionalWorldCupBracket(
  letters: readonly string[] = WORLD_CUP_GROUP_STAGE_LETTERS,
): KnockoutBracketModel {
  // Need a full slate of group labels to seed the Round of 32; otherwise fall back to the
  // official letters so the preview always shows a complete, well-formed bracket.
  const groupLetters = letters.length >= 12 ? letters.slice(0, 12) : WORLD_CUP_GROUP_STAGE_LETTERS

  const r32Labels = roundOf32Labels(groupLetters)

  const rounds: BracketRound[] = PROVISIONAL_ROUNDS.map((def, roundIndex) => {
    const isFinal = def.key === 'final'
    const matches: BracketMatch[] = []
    for (let tie = 0; tie < def.count; tie += 1) {
      const id = `prov-${def.key}-${tie}`
      if (roundIndex === 0) {
        // Round of 32 entrants come from the seeded group labels.
        matches.push(placeholderMatch(id, r32Labels[tie * 2], r32Labels[tie * 2 + 1], false))
      } else {
        // Later rounds advance the winners of the two feeding ties from the previous round.
        const prev = PROVISIONAL_ROUNDS[roundIndex - 1].label
        matches.push(
          placeholderMatch(
            id,
            `Winner of ${prev} ${tie * 2 + 1}`,
            `Winner of ${prev} ${tie * 2 + 2}`,
            isFinal,
          ),
        )
      }
    }
    return { key: def.key, label: def.label, matches }
  })

  const thirdPlace = placeholderMatch('prov-third-place', 'Loser of Semi-final 1', 'Loser of Semi-final 2', false)

  return { rounds, thirdPlace }
}
